// src/components/pages/admin-dash/PaymentsListByUser.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";
import { StatusBadge } from "./PaymentsListByFilter";

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : String(n ?? "—");

const getDisplayKind = (p) => {
  if (p?.transactionType) return String(p.transactionType).replaceAll("_", " ");
  return p?.creditPlan ? "ONE TIME PURCHASE" : "SUBSCRIPTION";
};
const getDisplayPlan = (p) =>
  p?.subscription?.plan || p?.subscriptionPlan || p?.creditPlan || "—";

export default function PaymentsListByUser() {
  const [userId, setUserId]   = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const handleSearch = async () => {
    if (!userId) {
      setError("Enter a user ID to search.");
      return;
    }
    setLoading(true);
    setError("");
    setPayments([]);
    try {
      const res = await axiosInstance.get(`/admin/payment/list-by-user/${userId}`);
      setPayments(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load payments for this user.");
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const total = payments.reduce(
    (acc, p) => acc + (Number(p.amount) || 0),
    0
  );

  const columns = [
    { key: "id",     label: "ID",     sort: true, width: "70px",
      render: (p) => <span className="tabular-nums" style={{ color: "var(--txt-2)" }}>#{p.id}</span> },
    { key: "kind",   label: "Kind",   sort: true, value: getDisplayKind,
      render: (p) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{getDisplayKind(p)}</span> },
    { key: "plan",   label: "Plan",   sort: true, value: getDisplayPlan,
      render: (p) => getDisplayPlan(p) },
    { key: "method", label: "Method", sort: true, value: (p) => p.paymentMethod || "",
      render: (p) => p.paymentMethod ? <Badge variant="neutral">{p.paymentMethod}</Badge> : "—" },
    { key: "status", label: "Status", sort: true, value: (p) => p.status || "",
      render: (p) => <StatusBadge status={p.status} /> },
    { key: "amount", label: "Amount", sort: true, align: "right", value: (p) => p.amount ?? 0,
      render: (p) => <span className="tabular-nums font-medium">{fmtUsd(p.amount)}</span> },
    { key: "txn",    label: "Txn",    sort: false,
      render: (p) => <span className="text-[11px] tabular-nums" style={{ color: "var(--txt-3)" }}>{p.idTransaction || "—"}</span> },
    { key: "date",   label: "Date",   sort: true, value: (p) => p.date || "",
      render: (p) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{p.date?.split("T")[0] || "—"}</span> },
  ];

  const firstUser = payments[0]?.user;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Payments by user
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Look up the complete payment history for any user.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field
            label="User ID"
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyDown={handleKey}
            placeholder="e.g. 1024"
            className="!mb-0"
          />
          <div className="flex items-end">
            <Button icon="search" onClick={handleSearch} loading={loading}>Search
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      {didSearch && payments.length > 0 && firstUser && (
        <Card variant="soft">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="lg-label mb-0.5">User</div>
              <div className="font-medium" style={{ color: "var(--txt)" }}>
                {firstUser.name || "—"}{" "}
                <span className="text-[12px]" style={{ color: "var(--txt-3)" }}>
                  · #{firstUser.id}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="lg-label mb-0.5">Lifetime spend</div>
              <div
                className="font-semibold tabular-nums"
                style={{ color: "var(--gold)", fontSize: 18 }}
              >
                {fmtUsd(total)}
              </div>
            </div>
            <div className="text-right">
              <div className="lg-label mb-0.5">Payments</div>
              <div
                className="font-semibold tabular-nums"
                style={{ color: "var(--txt)", fontSize: 18 }}
              >
                {payments.length}
              </div>
            </div>
          </div>
        </Card>
      )}

      <AdminTable
        columns={columns}
        rows={payments}
        loading={loading}
        emptyIcon="credit-card"
        emptyMessage={
          didSearch
            ? "No payments for this user."
            : "Enter a user ID above to begin."
        }
        defaultSort={{ key: "date", dir: "desc" }}
      />
    </div>
  );
}
